import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme
} from '@mui/material';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import {
  Visibility,
  VisibilityOff,
  Lock,
  LockReset as LockResetIcon,
  Close as CloseIcon,
  CheckCircle
} from '@mui/icons-material';
import { useAuth } from '../../common/hooks/useAuth';

/**
 * ResetPasswordModal Component
 * 
 * A modal dialog for resetting user password via email link.
 * Handles both requesting a password reset email and updating password with token.
 * 
 * Features:
 * - Request password reset email
 * - Update password with token from email link
 * - Password visibility toggle
 * - Form validation and error handling
 * - Loading states during operations
 * - Dark theme styling consistent with the app
 */
interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode?: 'request' | 'update'; // 'request' for email request, 'update' for password update
  token?: string; // Password reset token from URL
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode = 'request',
  token
}) => {
  const { resetPassword, updatePassword, error: authError, clearError } = useAuth();
  const theme = useTheme();
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirmPassword: false
  });

  const gradientText = {
    background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
  } as const;

  // Reset form when modal opens/closes or mode changes
  useEffect(() => {
    if (!open) {
      setFormData({ email: '', password: '', confirmPassword: '' });
      setTouched({ email: false, password: false, confirmPassword: false });
      setError('');
      setSuccess(false);
      clearError();
    }
  }, [open, mode, clearError]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear errors when user starts typing
    if (error) {
      setError('');
    }
    if (authError) {
      clearError();
    }
    if (success) {
      setSuccess(false);
    }
  };

  const handleBlur = (field: string) => () => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const validateRequestForm = () => {
    const errors: string[] = [];
    
    if (!formData.email) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    return errors;
  };

  const validateUpdateForm = () => {
    const errors: string[] = [];
    
    if (!formData.password) {
      errors.push('Password is required');
    } else if (formData.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }
    
    if (!formData.confirmPassword) {
      errors.push('Please confirm your password');
    } else if (formData.password !== formData.confirmPassword) {
      errors.push('Passwords do not match');
    }
    
    return errors;
  };

  const handleRequestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationErrors = validateRequestForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await resetPassword(formData.email);
      
      if (success) {
        setSuccess(true);
        // Auto-close after 3 seconds
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 3000);
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationErrors = validateUpdateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await updatePassword(formData.password);
      
      if (success) {
        setSuccess(true);
        // Auto-close after 2 seconds and redirect
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setError('Failed to update password. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ email: '', password: '', confirmPassword: '' });
      setTouched({ email: false, password: false, confirmPassword: false });
      setError('');
      setSuccess(false);
      clearError();
      onClose();
    }
  };

  const isRequestMode = mode === 'request';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
          borderRadius: 3,
          boxShadow: isDark ? '0 12px 48px rgba(0,0,0,0.8)' : '0 12px 48px rgba(0,0,0,0.3)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isDark 
              ? 'linear-gradient(180deg, rgba(11,11,11,0.95), rgba(0,0,0,0.95))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(245,245,247,0.95))'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LockResetIcon sx={{ color: '#000', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  ...gradientText,
                  fontWeight: 700,
                  mb: 0.5
                }}
              >
                {isRequestMode ? 'Reset Password' : 'Update Password'}
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {isRequestMode
                  ? 'Enter your email to receive a reset link'
                  : 'Enter your new password'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: '#ff1744',
                backgroundColor: 'rgba(255,23,68,0.15)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Success Alert */}
        {success && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{
                backgroundColor: 'rgba(0,180,255,0.15)',
                border: '1px solid rgba(0,180,255,0.3)',
                color: theme.palette.primary.main,
                '& .MuiAlert-icon': {
                  color: theme.palette.primary.main
                }
              }}
            >
              {isRequestMode
                ? 'If an account with that email exists, a password reset link has been sent. Please check your inbox.'
                : 'Password updated successfully! Redirecting...'}
            </Alert>
          </Box>
        )}

        {/* Error Alert */}
        {(error || authError) && !success && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert
              severity="error"
              sx={{
                backgroundColor: 'rgba(255,23,68,0.15)',
                border: '1px solid rgba(255,23,68,0.3)',
                color: '#ff1744',
                '& .MuiAlert-icon': {
                  color: '#ff1744'
                }
              }}
            >
              {error || authError}
            </Alert>
          </Box>
        )}

        {/* Form */}
        <Box
          component="form"
          onSubmit={isRequestMode ? handleRequestReset : handleUpdatePassword}
          sx={{ p: 3 }}
        >
          {isRequestMode ? (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              onBlur={handleBlur('email')}
              error={touched.email && !formData.email}
              helperText={touched.email && !formData.email ? 'Email is required' : ''}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? 'rgba(11,11,11,0.8)' : 'rgba(255,255,255,0.8)',
                  color: theme.palette.text.primary,
                  border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
                  '&:hover': {
                    borderColor: isDark ? 'rgba(0,180,255,0.5)' : 'rgba(0,180,255,0.4)'
                  },
                  '&.Mui-focused': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: isDark ? '0 0 8px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,180,255,0.2)'
                  },
                  '& fieldset': {
                    border: 'none'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.secondary,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main
                  }
                },
                '& .MuiFormHelperText-root': {
                  color: theme.palette.text.secondary
                }
              }}
              disabled={isLoading || success}
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                onBlur={handleBlur('password')}
                error={touched.password && !formData.password}
                helperText={touched.password && !formData.password ? 'Password is required' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        disabled={isLoading || success}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? 'rgba(11,11,11,0.8)' : 'rgba(255,255,255,0.8)',
                    color: theme.palette.text.primary,
                    border: isDark ? '1px solid rgba(138,43,226,0.3)' : '1px solid rgba(138,43,226,0.2)',
                    '&:hover': {
                      borderColor: isDark ? 'rgba(0,180,255,0.5)' : 'rgba(0,180,255,0.4)'
                    },
                    '&.Mui-focused': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: isDark ? '0 0 8px rgba(0,180,255,0.3)' : '0 0 8px rgba(0,180,255,0.2)'
                    },
                    '& fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    '&.Mui-focused': {
                      color: theme.palette.primary.main
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.text.secondary
                  }
                }}
                disabled={isLoading || success}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
                error={touched.confirmPassword && formData.password !== formData.confirmPassword}
                helperText={
                  touched.confirmPassword && formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        disabled={isLoading || success}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(11,11,11,0.8)',
                    color: '#FFFFFF',
                    border: '1px solid rgba(138,43,226,0.3)',
                    '&:hover': {
                      borderColor: 'rgba(0,180,255,0.5)'
                    },
                    '&.Mui-focused': {
                      borderColor: '#00B4FF',
                      boxShadow: '0 0 8px rgba(0,180,255,0.3)'
                    },
                    '& fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-focused': {
                      color: '#00B4FF'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
                disabled={isLoading || success}
              />
            </>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || success}
            startIcon={isLoading ? <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} /> : <LockResetIcon />}
            sx={{
              py: 1.5,
              mb: 3,
              borderRadius: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(90deg, #8A2BE2, #00B4FF)',
              color: '#000',
              '&:hover': {
                background: 'linear-gradient(90deg, #9A3BF2, #10C4FF)',
                boxShadow: '0 8px 24px rgba(0,180,255,0.35)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'rgba(138,43,226,0.3)',
                color: 'rgba(255,255,255,0.3)'
              },
              transition: 'all 0.25s ease'
            }}
          >
            {isLoading
              ? isRequestMode
                ? 'Sending...'
                : 'Updating...'
              : isRequestMode
              ? 'Send Reset Link'
              : 'Update Password'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;

